import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheckIcon, AlertCircleIcon, DollarSignIcon, SettingsIcon, ClockIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface PreAuthPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
}
export function PreAuthPayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange
}: PreAuthPaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [captureOption, setCaptureOption] = useState<'manual' | 'auto'>('manual');
  const [autoDays, setAutoDays] = useState('7');
  const [autoDaysOverridden, setAutoDaysOverridden] = useState(false);
  const [validityDays, setValidityDays] = useState(7);
  // Keep automatic-capture day count in sync with the pre-auth validity
  // unless the user has manually overridden it.
  useEffect(() => {
    if (!autoDaysOverridden) {
      setAutoDays(String(validityDays));
    } else {
      const current = parseInt(autoDays, 10);
      if (!Number.isNaN(current) && current > validityDays) {
        setAutoDays(String(validityDays));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validityDays, autoDaysOverridden]);
  const handleAmountChange = (v: string) => onAmountChange?.(v);
  const handleReferenceChange = (v: string) => onReferenceChange?.(v);
  const handleExpiryDateChange = (v: string) => {
    setExpiryDate(v);
    onExpiryDateChange?.(v);
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  return <div className="space-y-5" data-id="element-8502" data-mp="3rb7g55diphk4">
      {/* ─── PRE-AUTHORIZATION DETAILS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8503" data-mp="3r94ibz64ik4u">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-8504" data-mp="3w4ab9hxy0z2f">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8505" data-mp="4uutjoya7a3j">
            <ShieldCheckIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8506" data-mp="33e1thr7bkgod" />
            {t('components.PreAuthPayment.preauthorization_details')}
          </h2>
          <p className="text-xs text-gray-500 mt-1" data-id="element-8507" data-mp="38eph02wcafdu">
            {t('components.PreAuthPayment.place_a_temporary_hold_on_the')}
          </p>
        </div>

        <div className="p-6 space-y-4" data-id="element-8508" data-mp="3ibu2e0id26tl">
          <div data-id="element-8509" data-mp="3i8psqnm4mivj">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8510" data-mp="slgznaoq6kaw">
              {t('components.PreAuthPayment.hold_amount')}{' '}
              <span className="text-red-400" data-id="element-8511" data-mp="xiqnilwgunwm">*</span>
            </label>
            <div className="relative" data-id="element-8512" data-mp="2i1kpdxffr6de">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-8513" data-mp="2i4oxv6zxoo9v">
                <span className="text-sm font-medium text-gray-500" data-id="element-8514" data-mp="2d5m3cghky0g5">
                  {t('components.BulkPaymentListSimplified.usd')}
                </span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" onChange={e => handleAmountChange(e.target.value)} data-id="element-8515" data-mp="23d6m5le3xfwb" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-8516" data-mp="1pdf2p54tn7ax">
            <div data-id="element-8517" data-mp="1pg1clbf6pywb">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8518" data-mp="1keu4fb94qxp4">
                {t('components.BulkPaymentPreview.reference')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.PreAuthPayment.attrs.eg_booking_1234')} onChange={e => handleReferenceChange(e.target.value)} data-id="element-8519" data-mp="3jpln8ujlv0fw" />
            </div>
            <div data-id="element-8520" data-mp="22hjaz9sxqpvr">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-8521" data-mp="22i2j5yss9zwl">
                {t('components.BulkPaymentPreview.description')}
              </label>
              <input type="text" className={inputClass} placeholder={t('components.PreAuthPayment.attrs.eg_security_deposit')} data-id="element-8522" data-mp="1sqy28tjjs4z4" />
            </div>
          </div>
        </div>
      </div>

      {/* ─── PRE-AUTH VALIDITY ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8523" data-mp="1oqnyzon53sny">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-8524" data-mp="19sc33k7lcnxu">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8525" data-mp="19uybtn67x9hn">
            <ClockIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8526" data-mp="1ett197yoxwaq" />
            Pre-authorization validity
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-8527" data-mp="r4bc17awnfgm">
            Choose how long the hold remains valid before it automatically
            expires.
          </p>
        </div>
        <div className="px-6 py-5" data-id="element-8528" data-mp="155dxbhu2slqh">
          <div className="flex items-baseline justify-between mb-3" data-id="element-8529" data-mp="1059gfqp99ozq">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8530" data-mp="1072n35zvc97q">
              Hold valid for
            </label>
            <div className="flex items-baseline gap-1.5" data-id="element-8531" data-mp="1a3p75skdew8p">
              <span className="text-2xl font-semibold text-gray-900 tabular-nums" data-id="element-8532" data-mp="1a130dv5b0krj">
                {validityDays}
              </span>
              <span className="text-sm text-gray-500" data-id="element-8533" data-mp="1iuzcsi8o2itr">days</span>
            </div>
          </div>
          <input type="range" min={7} max={21} step={1} value={validityDays} onChange={e => setValidityDays(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#E2231A]" data-id="element-8534" data-mp="1xs8h0cj2jh2w" />
          <div className="flex justify-between text-xs text-gray-400 mt-2" data-id="element-8535" data-mp="24wzsemgw9870">
            <span data-id="element-8536" data-mp="29uj8lrx5jv1l">7 days</span>
            <span data-id="element-8537" data-mp="29u9k68rr9vy0">14 days</span>
            <span data-id="element-8538" data-mp="29t86dwjcuvcl">21 days</span>
          </div>
          <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-8539" data-mp="2emt73wcc23fh">
            <AlertCircleIcon size={14} className="text-amber-600 mt-0.5 flex-shrink-0" data-id="element-8540" data-mp="2jk2t120ijwyk" />
            <p className="text-xs text-amber-800" data-id="element-8541" data-mp="2noiwkwmnmsam">
              If not captured within {validityDays} day
              {validityDays === 1 ? '' : 's'}, the hold will be released and
              funds returned to the cardholder.
            </p>
          </div>
        </div>
      </div>

      {/* ─── CAPTURE OPTIONS ─── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-8542" data-mp="136clfla6qqk6">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-8543" data-mp="138fkeutbfu11">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-8544" data-mp="vomv1qepzh0l">
            <SettingsIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-8545" data-mp="qvbvmac8m2ip" />
            {t('components.PreAuthPayment.capture_options')}
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-8546" data-mp="luxn6r6q4qk5">
            {t('components.PreAuthPayment.choose_how_and_when_to_charge')}
          </p>
        </div>

        <div className={`flex items-center px-6 py-4 cursor-pointer transition-colors border-b border-gray-200 ${captureOption === 'manual' ? 'bg-[#E2231A]/5' : 'hover:bg-gray-50'}`} onClick={() => setCaptureOption('manual')} data-id="element-8547" data-mp="1ekwdsr0kgvqx">
          <input type="radio" name="captureOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0" checked={captureOption === 'manual'} onChange={() => setCaptureOption('manual')} data-id="element-8548" data-mp="zoox40odacge" />

          <div className="ml-4" data-id="element-8549" data-mp="dxnj6voj4txl">
            <p className="text-sm font-medium text-gray-900" data-id="element-8550" data-mp="dxe1dvdn1t2z">
              {t('components.PreAuthPayment.manual_capture')}
            </p>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-8551" data-mp="iy1tylhimhzb">
              {t('components.PreAuthPayment.charge_funds_manually_when_needed_from')}
            </p>
          </div>
        </div>

        <div className={`flex items-start px-6 py-4 cursor-pointer transition-colors ${captureOption === 'auto' ? 'bg-[#E2231A]/5' : 'hover:bg-gray-50'}`} onClick={() => setCaptureOption('auto')} data-id="element-8552" data-mp="90duji8ysmat">
          <input type="radio" name="captureOption" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0 mt-0.5" checked={captureOption === 'auto'} onChange={() => setCaptureOption('auto')} data-id="element-8553" data-mp="nwd3sw7t8shj" />

          <div className="ml-4 flex-1" data-id="element-8554" data-mp="1ke9d70z2vd6s">
            <p className="text-sm font-medium text-gray-900" data-id="element-8555" data-mp="1kfamp10g3rme">
              {t('components.PreAuthPayment.automatic_capture')}
            </p>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-8556" data-mp="305bk7pruz3rm">
              {t('components.PreAuthPayment.automatically_charge_the_held_amount_after')}
            </p>
            {captureOption === 'auto' && <div className="mt-3 flex items-center gap-2" data-id="element-8557" data-mp="39w680ykr6bf6">
                <label className="text-xs text-gray-500" data-id="element-8558" data-mp="2m50q8dv95928">
                  {t('components.PreAuthPayment.capture_after')}
                </label>
                <input type="number" className="w-16 px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-center focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" value={autoDays} onChange={e => {
              setAutoDays(e.target.value);
              setAutoDaysOverridden(true);
            }} min="1" max={validityDays} onClick={e => e.stopPropagation()} data-id="element-8559" data-mp="2h7qw4kwx4982" />

                <span className="text-xs text-gray-500" data-id="element-8560" data-mp="3ey1nf3jctpm7">
                  {t('components.PreAuthPayment.days')}
                </span>
                {autoDaysOverridden && <button type="button" onClick={e => {
              e.stopPropagation();
              setAutoDaysOverridden(false);
              setAutoDays(String(validityDays));
            }} className="ml-1 text-[11px] text-[#9F140F] hover:underline" data-id="element-8561" data-mp="358rs7et7rqtv">
                    Reset to validity ({validityDays}d)
                  </button>}
                {!autoDaysOverridden && <span className="ml-1 text-[11px] text-gray-400" data-id="element-8562" data-mp="pi5maxbm2vef">
                    Matches validity window
                  </span>}
              </div>}
          </div>
        </div>
      </div>

      {/* ─── ADDITIONAL OPTIONS ─── */}
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} data-id="element-8563" data-mp="rzz74oiqe5gc" />
    </div>;
}